import type {
  AudiencePageStoryblok,
  BlockAdviceStoryblok,
  BlockCampaignHeaderStoryblok,
  BlockCampaignQuickLinksStoryblok,
  BlockCardsStoryblok,
  BlockColumnStoryblok,
  BlockColumnsStoryblok,
  BlockContentCardStoryblok,
  BlockContentSecondaryCardListStoryblok,
  BlockContentSecondaryCardStoryblok,
  BlockCustomSectionStoryblok,
  BlockDownloadsListStoryblok,
  BlockFeatureStoryblok,
  BlockFormStoryblok,
  BlockMailchimpFormStoryblok,
  BlockPromoBannerStoryblok,
  BlockResourceCardStoryblok,
  BlockRichTextSimpleStoryblok,
  BlockRichTextStoryblok,
  BlockToolGetHelpStoryblok,
  BlockToolQuickLinksStoryblok,
  BlockToolServiceProvidersStoryblok,
  BlockVideoStoryblok,
  ContentTypeAuthorStoryblok,
  CustomCardStoryblok,
  EventsStoryblok,
  FeaturedTopicPageStoryblok,
  GlossaryPageStoryblok,
  GlossaryTermStoryblok,
  LandingPageStoryblok,
  ListPageStoryblok,
  NewsStoryblok,
  PageIntroStoryblok,
  PageStoryblok,
  PodcastSeriesStoryblok,
  QuickAccessResultsPageStoryblok,
  RealStoriesStoryblok,
  ResourceStoryblok,
  RichtextStoryblok,
  SearchStoryblok,
  ServiceProvidersStoryblok,
} from '~/types/storyblok'

export type StoryContentBlok =
  | AudiencePageStoryblok
  | BlockAdviceStoryblok
  | BlockCampaignHeaderStoryblok
  | BlockCampaignQuickLinksStoryblok
  | BlockCardsStoryblok
  | BlockColumnsStoryblok
  | BlockColumnStoryblok
  | BlockContentCardStoryblok
  | BlockContentSecondaryCardListStoryblok
  | BlockContentSecondaryCardStoryblok
  | BlockCustomSectionStoryblok
  | BlockDownloadsListStoryblok
  | BlockFeatureStoryblok
  | BlockFormStoryblok
  | BlockMailchimpFormStoryblok
  | BlockPromoBannerStoryblok
  | BlockResourceCardStoryblok
  | BlockRichTextSimpleStoryblok
  | BlockRichTextStoryblok
  | BlockToolGetHelpStoryblok
  | BlockToolQuickLinksStoryblok
  | BlockToolServiceProvidersStoryblok
  | BlockVideoStoryblok
  | ContentTypeAuthorStoryblok
  | CustomCardStoryblok
  | EventsStoryblok
  | FeaturedTopicPageStoryblok
  | GlossaryPageStoryblok
  | GlossaryTermStoryblok
  | LandingPageStoryblok
  | ListPageStoryblok
  | NewsStoryblok
  | PageIntroStoryblok
  | PageStoryblok
  | PodcastSeriesStoryblok
  | QuickAccessResultsPageStoryblok
  | RealStoriesStoryblok
  | ResourceStoryblok
  | RichtextStoryblok
  | SearchStoryblok
  | ServiceProvidersStoryblok

export type UseStoryContentProps = {
  blok: Ref<StoryContentBlok>
  customStory?: Ref<StoryContentBlok | undefined>
  storyContent?: Ref<StoryContentBlok | undefined>
}

export default function useStoryContent({
  blok,
  customStory = ref(),
  storyContent: storyContentOverride = ref(),
}: UseStoryContentProps) {
  const site = useSiteConfig()
  const { story } = storeToRefs(useStoryProviderStore())

  const isUsingRichTextIntroduction = ref(false)

  const storyContent = computed(() => storyContentOverride?.value || blok.value)

  const pageId = computed(() => story.value?.id)

  const storyUid = computed(() => blok.value?._uid)

  const pageBanner = computed(
    () =>
      storyContent.value?.intro?.[0] || storyContent.value?.content?.intro?.[0],
  )

  const pageBannerTitle = computed(() => pageBanner.value?.title)

  const pageBannerComponent = computed(() => {
    if (pageBanner.value?.component === 'BlockCampaignHeader')
      return pageBanner.value.component
    return pageBanner.value?.type !== 'feature' ? 'PageIntro' : 'ArticleIntro'
  })

  const pageComponentType = computed(() => story.value?.content?.component)

  const references = computed(() => blok.value?.references)

  const authors = computed(() => blok.value?.authors)

  const downloads = computed(() => storyContent.value?.downloads)

  const blocks = computed(() => storyContent.value?.blocks || [])

  const richTextContent = computed(() => storyContent.value?.content)

  const customTitle = computed(() => customStory.value?.title)

  const customIntroduction = computed(() => customStory.value?.introduction)

  const customLink = computed(() => customStory.value?.url)

  const states = computed(() => storyContent.value?.states)

  const resourceType = computed(() => storyContent.value?.type)
  const richTextIntroduction = computed(() => {
    if (richTextContent.value?.content?.content) {
      const text = richTextContent.value?.content?.content
        ?.map((node) => {
          return (
            node?.content?.[0]?.text || node?.content?.[0]?.content?.[0]?.text
          )
        })
        .join(' ')
      return trimText(text, 155)
    }
    return ''
  })

  const title = computed<string>(
    () =>
      customTitle.value ||
      pageBannerTitle.value ||
      blok.value?.title ||
      //TODO: Verify if switching these two lines is correct
      // Previous implementation had story.name first
      blok.value?.name ||
      story.value?.name ||
      '',
  )

  const introduction = computed(
    () =>
      customIntroduction.value ||
      storyContent.value?.preview ||
      storyContent.value?.description ||
      pageBanner.value?.introduction ||
      storyContent.value?.quote ||
      getRichTextIntroduction(),
  )

  const customRelatedContent = computed(() => blok.value?.related?.[0])

  const customCta = computed(() => customStory.value?.cta)

  const cta = computed(() => customCta.value || 'Learn more')

  const sectionClass = computed(() => {
    return blok.value?.paddings
      ? 'section-' + blok.value?.paddings
      : 'section-default'
  })

  const hasRichTextContent = computed(() => {
    const richtextContent = blok.value?.content
    if (richtextContent?.type !== 'doc') {
      return false
    }

    return (
      undefined !==
      richtextContent?.content?.find(
        (item) => item.content?.length || item.type === 'blok',
      )
    )
  })

  const hasArticleIntroduction = computed(
    () => introduction.value && !isUsingRichTextIntroduction.value,
  )

  const hideComments = computed(
    () =>
      blok.value?.hide_comments || !pageId.value || !hasRichTextContent.value,
  )

  const hideRelated = computed(() => blok.value?.hide_related || !pageId.value)

  const pageContainsSubscriptionForm = computed(
    () => !!blok.value?.subscription,
  )

  const shouldShowStickyNavigation = computed(
    () => !!blok.value?.shouldShowPageNavigation,
  )

  // TODO: Refactor to use isStoryOfType from ~/utils/storyTypeGuards
  const isGlossaryTerm = computed(
    () => storyContent.value.component === 'GlossaryTerm',
  )

  const glossaryTermIntroduction = computed<string>(() => {
    if (isGlossaryTerm.value) {
      return storyContent.value.description?.content?.[0]?.content
        .map((node) => {
          return node?.text
        })
        .join(' ')
    }
    return ''
  })

  function getRichTextIntroduction() {
    isUsingRichTextIntroduction.value = true
    return richTextIntroduction.value
  }

  const audiences = computed(() => {
    const audiences = storyContent.value?.audiences
    // return empty array if undefined
    if (!audiences) return []
    // cast any single-option string to array
    return Array.isArray(audiences) ? audiences : [audiences]
  })

  const topics = computed(() => {
    const topics = storyContent.value?.topics
    if (!topics) return []
    return Array.isArray(topics) ? topics : [topics]
  })

  const media = computed((): Media => {
    const mediaSearchLocations = [
      customImage.value,
      graphicImage.value,
      pageBanner.value?.image,
      blok.value?.media,
      searchCardImage.value,
    ]
    return (
      mediaSearchLocations.find((search) => search?.filename) ||
      fallbackImage.value
    )
  })

  const mediaFocalPoint = computed(() => pageBanner.value?.image?.focus || '')

  const fallbackImage = computed(() => {
    const filename = site.image || ''
    return { filename }
  })

  const customImage = computed(() => customStory.value?.image)

  const graphicImage = computed(
    () =>
      storyContent.value?.graphic_image ||
      storyContent.value?.image ||
      storyContent.value?.logo ||
      storyContent.value?.photo,
  )

  const searchCardImage = computed(() => {
    const url =
      blok?.value?.bannerImage ||
      blok?.value?.image ||
      graphicImage.value ||
      fallbackImage.value.filename
    return { filename: url }
  })

  const isDefaultTagsSorting = computed(
    () => !!storyContent.value?.disable_tags_sorting,
  )

  const meta = computed(() => {
    switch (storyContent.value?.component) {
      case 'ServiceProviders':
        return serviceProvidersMeta.value
      case 'Resource':
        return resourceMeta.value
      default:
        return { url: storyContent.value?.url }
    }
  })

  const serviceProvidersMeta = computed(() => ({
    phones: storyContent.value?.phones?.map((phone) => phone.phone) || [],
    phone: storyContent.value?.phone || '',
    phoneAlternate: storyContent.value?.phone_alternate || '',
    email: storyContent.value?.email || '',
    hours: storyContent.value?.hours || '',
    url: storyContent.value?.url,
  }))

  const resourceMeta = computed(() => ({
    link: storyContent.value?.link,
    url: storyContent.value?.url,
  }))

  const isPrimaryAudience = computed(
    () => !!storyContent.value?.primary_tag_audience,
  )

  const isGraphicImage = computed(
    () => !!(graphicImage.value?.filename && !customImage.value),
  )

  const isLogoImage = computed(
    () => !!(storyContent.value?.logo && !customImage.value),
  )

  const showTitleOnOgImage = computed(
    () => !!storyContent.value?.showTitleOnOgImage,
  )

  // TODO: Refactor to use isStoryOfType from ~/utils/storyTypeGuards
  const mediaFit = computed(() =>
    isGraphicImage.value ||
    isLogoImage.value ||
    storyContent.value?.component === 'Resource'
      ? 'contain'
      : 'cover',
  )

  const mediaBgColor = computed(() =>
    isGraphicImage.value &&
    storyContent.value?.graphic_image_bg_color?.color &&
    storyContent.value?.graphic_image_bg_color?.color !== '#f40000'
      ? storyContent.value?.graphic_image_bg_color.color
      : '#ECEAEF',
  )

  const seoGraphicMedia = computed((): string => {
    if (!isGraphicImage.value) {
      return ''
    }

    const width = 600
    const height = 600
    const quality = 75
    const format = 'png'
    const fit = 'fit-in'
    const focal = mediaFocalPoint.value

    return mediaService(
      graphicImage.value.filename,
      width,
      height,
      quality,
      format,
      mediaBgColor.value.substr(1),
      fit,
      focal,
    )
  })

  return {
    audiences,
    authors,
    blocks,
    cta,
    customLink,
    customRelatedContent,
    downloads,
    fallbackImage,
    glossaryTermIntroduction,
    graphicImage,
    hasArticleIntroduction,
    hasRichTextContent,
    hideComments,
    hideRelated,
    introduction,
    isDefaultTagsSorting,
    isPrimaryAudience,
    media,
    mediaBgColor,
    mediaFit,
    mediaFocalPoint,
    meta,
    pageBanner,
    pageBannerComponent,
    pageComponentType,
    pageContainsSubscriptionForm,
    pageId,
    references,
    resourceType,
    richTextContent,
    sectionClass,
    seoGraphicMedia,
    shouldShowStickyNavigation,
    showTitleOnOgImage,
    states,
    storyUid,
    title,
    topics,
  }
}
